/**
 * Modal — Verificar compatibilidade externa (ERP LCMY_GO_PART_CATALOG).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconBuilding } from '@tabler/icons-react';
import { BoardClickUpFilterChipDropdown } from '../../board/toolbar/BoardClickUpFilterChipDropdown';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/components/BoardEncaminharChamadoFlow.css';
import '../../estoque/components/KitCreateKitModal.css';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import '../../go-config/components/GoHoverTooltip.css';
import { fetchCompatExternalCompat } from './compatEquipApi';
import './CompatExternalCompatModal.css';

function ExtCheckbox({ checked, onChange, title, disabled, indeterminate = false }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);

  const control = (
    <label
      className={'vp-ext-check-label' + (disabled ? ' is-disabled' : '')}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        ref={ref}
        type="checkbox"
        className="vp-ext-check-native"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        aria-label={title || 'Selecionar'}
      />
      <span className="vp-ext-check-box" aria-hidden />
    </label>
  );
  if (!title) return control;
  return (
    <GoHoverTooltip text={title} align="center" delayMs={160}>
      {control}
    </GoHoverTooltip>
  );
}

function toggleSelection(prev, value) {
  return prev.includes(value) ? prev.filter((x) => x !== value) : prev.concat([value]);
}

function filterSelectionDisplay(selected, singular, plural) {
  if (!selected.length) return '';
  if (selected.length === 1) return selected[0];
  return `${selected.length} ${plural}`;
}

export default function CompatExternalCompatModal({
  open,
  equipmentCode,
  equipmentName,
  alreadyLinkedCodes = [],
  onClose,
  onImport,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandSel, setBrandSel] = useState([]);
  const [query, setQuery] = useState('');
  const [openFilter, setOpenFilter] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);

  const linkedSet = useMemo(
    () => new Set((alreadyLinkedCodes || []).map((c) => String(c))),
    [alreadyLinkedCodes]
  );

  useEffect(() => {
    if (!open || !equipmentCode) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSelected(new Set());
    setBrandSel([]);
    setQuery('');
    setOpenFilter(null);
    fetchCompatExternalCompat(equipmentCode)
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items || []);
        setBrandOptions(data?.facets?.brands || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setBrandOptions([]);
        setError(err.message || 'Falha ao carregar compatibilidade externa');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, equipmentCode]);

  const availableItems = useMemo(
    () => items.filter((p) => !linkedSet.has(p.productCode)),
    [items, linkedSet]
  );

  const brandOptionsEffective = useMemo(() => {
    const fromAvailable = Array.from(new Set(availableItems.map((i) => i.brand).filter(Boolean))).sort(
      (a, b) => a.localeCompare(b, 'pt-BR')
    );
    return fromAvailable.length ? fromAvailable : brandOptions;
  }, [availableItems, brandOptions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return availableItems.filter((p) => {
      if (brandSel.length && !brandSel.includes(p.brand)) return false;
      if (!q) return true;
      const hay = `${p.description} ${p.productCode} ${p.reference} ${p.barcode} ${p.brand} ${p.unit}`.toLowerCase();
      return hay.includes(q);
    });
  }, [availableItems, brandSel, query]);

  const selectableCodes = useMemo(
    () => filtered.map((p) => p.productCode),
    [filtered]
  );

  const allVisibleSelected =
    selectableCodes.length > 0 && selectableCodes.every((c) => selected.has(c));
  const someVisibleSelected =
    !allVisibleSelected && selectableCodes.some((c) => selected.has(c));

  const toggleOne = (code, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(code);
      else next.delete(code);
      return next;
    });
  };

  const toggleAllVisible = (on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      selectableCodes.forEach((c) => {
        if (on) next.add(c);
        else next.delete(c);
      });
      return next;
    });
  };

  const handleImport = async () => {
    const codes = Array.from(selected);
    if (!codes.length || importing) return;
    setImporting(true);
    try {
      const picked = items.filter((p) => selected.has(p.productCode));
      await onImport?.(picked);
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  const nSel = selected.size;

  return (
    <div
      className="board-encaminhar-backdrop kit-create-kit-backdrop kit-create-kit-backdrop--products vp-ext-compat-backdrop"
      onClick={() => !importing && onClose?.()}
    >
      <div
        className="board-encaminhar-panel kit-create-kit-panel kit-create-kit-panel--products vp-ext-compat-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vp-ext-compat-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kit-create-kit-title-row">
          <h4 id="vp-ext-compat-title" className="board-encaminhar-panel-title kit-create-kit-title">
            Verificar compatibilidade externa
            <span className="board-encaminhar-panel-os kit-create-kit-title-os">
              {equipmentName || equipmentCode}
              <span className="kit-create-kit-selected-suffix"> · selecione os produtos</span>
            </span>
          </h4>
          <button
            type="button"
            className="kit-create-kit-close-btn"
            aria-label="Fechar"
            disabled={importing}
            onClick={() => onClose?.()}
          >
            ×
          </button>
        </div>

        <div className="vp-ext-compat-toolbar">
          <div className="filter-buttons vp-ext-compat-filters">
            <BoardClickUpFilterChipDropdown
              label="Fabricante"
              icon={IconBuilding}
              options={brandOptionsEffective}
              selected={brandSel}
              selectionDisplay={filterSelectionDisplay(brandSel, 'fabricante', 'fabricantes')}
              isOpen={openFilter === 'brand'}
              onOpenChange={(open) => setOpenFilter(open ? 'brand' : null)}
              onToggleItem={(v) => setBrandSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setBrandSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setBrandSel([])}
              onApply={() => setOpenFilter(null)}
              disabled={loading || importing}
              pickerMode="simple"
              pickerWidth={280}
              pickerMaxHeight={360}
              searchPlaceholder="Buscar fabricante"
              countLabel="fabricantes"
              listAriaLabel="Fabricantes"
              emptyMessage="Nenhum fabricante"
              showSearch={brandOptionsEffective.length > 8}
            />
            {(brandSel.length > 0 || query.trim()) && (
              <button
                type="button"
                className="user-form-link-btn"
                disabled={loading || importing}
                onClick={() => {
                  setBrandSel([]);
                  setQuery('');
                }}
              >
                Limpar filtros
              </button>
            )}
          </div>
          <input
            className="vp-ext-compat-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar descrição, código ou referência…"
            disabled={loading || importing}
          />
        </div>

        <div className="kit-create-kit-products-wrap vp-ext-compat-list-wrap">
          {loading ? (
            <div className="vp-ext-compat-empty">Carregando produtos do ERP…</div>
          ) : error ? (
            <div className="vp-ext-compat-empty is-error">{error}</div>
          ) : !filtered.length ? (
            <div className="vp-ext-compat-empty">
              {availableItems.length === 0 && items.length
                ? 'Todos os produtos externos deste modelo já foram importados.'
                : 'Nenhum produto compatível encontrado para este modelo.'}
            </div>
          ) : (
            <div
              className={'vp-ext-compat-table' + (nSel ? ' is-bulk-selection' : '')}
              role="table"
            >
              <div className="vp-ext-compat-thead" role="row">
                <span className="vp-ext-compat-th-check">
                  <ExtCheckbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    disabled={importing || selectableCodes.length === 0}
                    title="Selecionar todos visíveis"
                    onChange={toggleAllVisible}
                  />
                </span>
                <span className="vp-ext-compat-th">Código</span>
                <span className="vp-ext-compat-th">Referência</span>
                <span className="vp-ext-compat-th">Descrição</span>
                <span className="vp-ext-compat-th">Fabricante</span>
                <span className="vp-ext-compat-th">Unidade</span>
              </div>
              {filtered.map((p) => {
                const on = selected.has(p.productCode);
                return (
                  <div
                    key={p.productCode}
                    role="row"
                    className={'vp-ext-compat-row' + (on ? ' is-selected' : '')}
                    onClick={() => !importing && toggleOne(p.productCode, !on)}
                  >
                    <span className="vp-ext-compat-check-cell">
                      <ExtCheckbox
                        checked={on}
                        disabled={importing}
                        title="Selecionar"
                        onChange={(c) => toggleOne(p.productCode, c)}
                      />
                    </span>
                    <span className="vp-ext-compat-cell vp-ext-compat-cell--mono">{p.productCode}</span>
                    <span className="vp-ext-compat-cell vp-ext-compat-cell--mono">{p.reference || '—'}</span>
                    <span className="vp-ext-compat-cell vp-ext-compat-cell--name">{p.description}</span>
                    <span className="vp-ext-compat-cell">{p.brand || '—'}</span>
                    <span className="vp-ext-compat-cell">{p.unit || '—'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="board-encaminhar-actions kit-create-kit-actions">
          <div className="kit-create-kit-sel" aria-live="polite">
            {nSel ? `${nSel} selecionado(s)` : `${filtered.length} produto(s)`}
          </div>
          <button
            type="button"
            className="user-form-link-btn user-form-footer-btn"
            disabled={importing}
            onClick={() => onClose?.()}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            disabled={importing || nSel === 0}
            onClick={handleImport}
          >
            {importing ? 'Importando…' : 'Importar para compatibilidade'}
          </button>
        </div>
      </div>
    </div>
  );
}
